/** OAuth BUS only. No user-session state, persistent storage or background renewal. */
export interface BusToken {
  access_token: string;
  token_type: string;
  expires_in?: number | string;
  [key: string]: unknown;
}

export class BusTokenError extends Error {
  constructor(public retryable = false) {
    super('No se pudo autenticar con el BUS');
    Object.setPrototypeOf(this, BusTokenError.prototype);
    this.name = 'BusTokenError';
  }
}

export function createBusTokenManager(
  load: () => Promise<BusToken>,
  now: () => number = Date.now,
  monotonic: () => number = () => performance.now()
) {
  let cached: { token: BusToken; issued: number; wall: number; mono: number } | undefined;
  let pending: Promise<BusToken> | undefined;

  // JWT exp is only a conservative expiry hint, never a signature/authentication check.
  function jwtExpiry(value: string): number | undefined {
    try {
      const parts = value.split('.');
      if (parts.length !== 3) return undefined;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const exp = JSON.parse(atob(payload)).exp;
      return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : undefined;
    } catch (_) { return undefined; }
  }

  async function acquire(reusable: boolean): Promise<BusToken> {
    for (let attempt = 0; attempt < 2; attempt++) {
      const started = now(), startedMono = monotonic();
      try {
        const raw = await load();
        if (!raw || typeof raw.access_token !== 'string' || !raw.access_token.trim() ||
            /\s/.test(raw.access_token) || typeof raw.token_type !== 'string' ||
            raw.token_type.toLowerCase() !== 'bearer') throw new BusTokenError(true);
        const token = { ...raw, token_type: 'Bearer' };
        let lifetime: number | undefined;
        if (raw.expires_in !== undefined) {
          if ((typeof raw.expires_in !== 'number' && typeof raw.expires_in !== 'string') ||
              !/^\d+(?:\.\d+)?$/.test(String(raw.expires_in))) throw new BusTokenError(true);
          const seconds = Number(raw.expires_in);
          if (!Number.isFinite(seconds) || seconds <= 0) throw new BusTokenError(true);
          lifetime = seconds * 1000;
        }
        const exp = jwtExpiry(token.access_token);
        if (exp !== undefined) lifetime = Math.min(lifetime === undefined ? Infinity : lifetime, exp - started);
        if (lifetime !== undefined) {
          // Reserve 15s, regardless of TTL; exceptionally short tokens reserve at most half.
          const usable = lifetime - Math.min(15000, lifetime / 2);
          if (!Number.isFinite(usable) || usable <= 0 || now() < started ||
              now() >= started + usable || monotonic() >= startedMono + usable) {
            throw new BusTokenError(true);
          }
          if (reusable) cached = { token, issued: started, wall: started + usable, mono: startedMono + usable };
        } else if (reusable) {
          // OAuth expires_in is optional: without a lifetime, share only this acquisition.
          cached = undefined;
        }
        return { ...token };
      } catch (error) {
        if (attempt === 1 || !(error instanceof BusTokenError) || !error.retryable) {
          throw new BusTokenError();
        }
      }
    }
    throw new BusTokenError();
  }

  return {
    async get(): Promise<BusToken> {
      if (cached && now() >= cached.issued && now() < cached.wall && monotonic() < cached.mono) {
        return { ...cached.token };
      }
      if (!pending) {
        cached = undefined;
        pending = acquire(true).finally(() => { pending = undefined; });
      }
      return { ...await pending };
    },
    // Each write/upload starts its own acquisition, independent of the GET cache/flight.
    fresh(): Promise<BusToken> { return acquire(false); },
    invalidate(rejectedToken: string) {
      // A delayed 401 from an older request must not evict an already renewed token.
      if (cached && cached.token.access_token === rejectedToken) cached = undefined;
    },
  };
}

/** Only GET reuses/retries. Every other method acquires a fresh token and sends once. */
export async function fetchWithBusRecovery(
  url: string, options: RequestInit,
  manager: ReturnType<typeof createBusTokenManager>,
  send: typeof fetch = fetch
): Promise<Response> {
  const headers = new Headers(options.headers);
  const reusable = (options.method || 'GET').toUpperCase() === 'GET';
  // Never use Authorization captured when a form was opened.
  const token = (await (reusable ? manager.get() : manager.fresh())).access_token;
  headers.set('Authorization', 'Bearer ' + token);
  const config = { ...options, headers, redirect: 'error' as RequestRedirect };
  let response = await send(url, config);
  if (response.status !== 401) return response;
  manager.invalidate(token);
  if (!reusable) return response;
  if (options.signal && options.signal.aborted) return response;
  const renewed = await manager.get();
  const retryHeaders = new Headers(headers);
  retryHeaders.set('Authorization', 'Bearer ' + renewed.access_token);
  response = await send(url, { ...config, headers: retryHeaders });
  if (response.status === 401) manager.invalidate(renewed.access_token);
  return response;
}
